import React, { useState } from "react";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import WebIcon from "@mui/icons-material/Web";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChatIcon from "@mui/icons-material/Chat";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import DescriptionIcon from "@mui/icons-material/Description";
import TagIcon from "@mui/icons-material/Tag";
import TitleIcon from "@mui/icons-material/Title";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import { styled, useTheme, useMediaQuery, Tooltip } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  InsightsResponse,
  RegenerateInsightsResponse,
} from "../../services/episode.service";
import { formatLabel } from "../../../../utils/formatLabel";
import Quote from "./parts/Quote";
import Title from "./parts/Title";
import ChapterView from "./parts/ChapterView";
import ContentPost from "./parts/ContentPost";
import { Category } from "../../../common/types";
import { episodeService } from "../../services";
import RegenerateInsightsDialog from "./RegenerateInsightsModal";
import NewsletterPost from "./parts/NewsletterPost";
import CreditsWarningModal from "../../../common/CreditsWarningModal";
import { API_ERROR_MESSAGE } from "../../../../constants";
import { usersService } from "../../../common/Headerbar/services";

const StyledTabs = styled(Tabs)`
  &.MuiTabs-vertical {
    min-width: 200px;
    max-width: 200px;
  }
`;

interface TabPanelProps {
  index: number;
  value: number;
  children?: React.ReactNode;
  isRegeneratingInsights?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, isRegeneratingInsights, ...other } = props;

  if (isRegeneratingInsights) {
    return (
      <Box
        sx={{ display: "flex", marginTop: "64px", justifyContent: "center" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1, width: "100%" }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

interface Props {
  insights?: InsightsResponse | undefined;
  refetch: () => void;
  episodeId: string;
}

export type Name =
  | "tweets"
  | "chapters"
  | "blog_post"
  | "newsletter"
  | "quotes"
  | "titles"
  | "keywords"
  | "descriptions"
  | "linkedin_posts";

export default function Insights({ insights, refetch, episodeId }: Props) {
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [isRegenerateInsightsModalOpen, setIsRegenerateInsightsModalOpen] =
    useState<boolean>(false);
  const [regeneratingInsightsForIndex, setRegeneratingInsightsForIndex] =
    useState<number | null>(null);
  const [creditsWarningModalOpen, setCreditsWarningModalOpen] =
    useState<boolean>(false);
  const [customPrompt, setCustomPrompt] = useState<string>("");

  const { data: userData } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  const availableCredits =
    userData?.organization?.subscription?.available_credits || 0;
  const hasCredits = availableCredits >= 5;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { mutate, isPending } = useMutation({
    mutationKey: ["regenerateInsights"],
    mutationFn: (values: RegenerateInsightsResponse) =>
      episodeService.regenerateInsights(episodeId, values),
    onSuccess: () => {
      refetch();
      setRegeneratingInsightsForIndex(null);
      setIsRegenerateInsightsModalOpen(false);
      setCustomPrompt("");
    },
    onError: (err) => {
      if (err && typeof err === "object") {
        if ("statusCode" in err) {
          const apiError = err as {
            statusCode?: number;
            message?: string;
            error?: string;
          };
          if (apiError.statusCode === 429) {
            if (
              apiError.error ===
              API_ERROR_MESSAGE.MAXIMUM_NUMBER_OF_PROMPTS_REACHED
            ) {
              setCreditsWarningModalOpen(true);
            } else if (
              apiError.error === API_ERROR_MESSAGE.INSUFFICIENT_BALANCE
            ) {
              toast.error(`${apiError.error}: ${apiError.message}`);
            }
          }
        }
      }
    },
  });

  const tabIcons = {
    tweets: ChatIcon,
    chapters: MenuBookIcon,
    blog_post: WebIcon,
    newsletter: NewspaperIcon,
    quotes: FormatQuoteIcon,
    linkedin_posts: LinkedInIcon,
    titles: TitleIcon,
    keywords: TagIcon,
    descriptions: DescriptionIcon,
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };

  const tabNames: string[] = insights ? Object.keys(insights) : [];
  const tabs = tabNames.map((name: string) => {
    const Icon = tabIcons[name as Name];
    return {
      name,
      icon: Icon,
    };
  });

  const selectedTab = tabNames[selectedTabIndex];

  const handleRegenerateInsights = (prompt?: string) => {
    setRegeneratingInsightsForIndex(selectedTabIndex);
    setCustomPrompt(prompt || "");
    mutate({
      category: selectedTab,
      custom_prompt: prompt,
    });
  };

  const handleReloadData = async () => {
    await refetch();
  };

  const handleConfirmCreditsWarning = () => {
    mutate({
      category: selectedTab,
      custom_prompt: customPrompt,
      allow_additional_charges: true,
    });
  };

  return (
    <>
      <CreditsWarningModal
        open={creditsWarningModalOpen}
        onClose={() => setCreditsWarningModalOpen(false)}
        onConfirm={handleConfirmCreditsWarning}
      />
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          height: "100%",
          width: "100%",
        }}
      >
        <StyledTabs
          orientation={isMobile ? "horizontal" : "vertical"}
          variant="scrollable"
          value={selectedTabIndex}
          onChange={handleChange}
          aria-label={isMobile ? "Horizontal tabs" : "Vertical tabs example"}
          sx={{
            width: isMobile ? "100%" : undefined,
            borderBottom: isMobile ? 1 : 0,
            borderRight: isMobile ? 0 : 1,
            borderColor: "divider",
            mb: isMobile ? 2 : 0,
          }}
        >
          {tabs.length > 0
            ? tabs.map((tab, index) => {
                const Icon = tab.icon;
                return (
                  <Tab
                    key={tab.name}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>{formatLabel(tab.name)}</span>
                        <Tooltip
                          title={`Regenerate ${formatLabel(tab.name)}`}
                          placement="right"
                        >
                          <Box
                            component="div"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.currentTarget.blur(); // Remove focus to prevent aria-hidden conflicts
                              setSelectedTabIndex(index);
                              setIsRegenerateInsightsModalOpen(true);
                            }}
                            sx={{
                              color: "#1976d2",
                              p: 0.5,
                              borderRadius: 1,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: "32px",
                              minHeight: "32px",
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.1)",
                                // Remove transform scale to prevent ResizeObserver loops
                                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
                              },
                              "&:focus": {
                                outline: "2px solid #1976d2",
                                outlineOffset: "2px",
                              },
                              transition: "all 0.2s ease-in-out",
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Regenerate ${formatLabel(tab.name)}`}
                          >
                            <AutoModeIcon sx={{ fontSize: "20px" }} />
                          </Box>
                        </Tooltip>
                      </Box>
                    }
                    {...a11yProps(index)}
                    sx={{
                      fontSize: isMobile ? "13px" : "12px",
                      alignSelf: isMobile ? "center" : "start",
                      minWidth: isMobile ? 90 : undefined,
                      width: "100%",
                      textAlign: "left",
                    }}
                    icon={<Icon sx={{ fontSize: "18px" }} />}
                    iconPosition="start"
                  />
                );
              })
            : null}
        </StyledTabs>
        <Box sx={{ width: "100%", px: isMobile ? 0 : 2 }}>
          <Box
            sx={{
              width: "100%",
              mx: isMobile ? 0 : "auto",
              mt: isMobile ? 2 : 0,
            }}
          >
            <TabPanel
              value={selectedTabIndex}
              index={0}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 0
              }
            >
              {insights &&
                insights.quotes.length > 0 &&
                insights.quotes.map((quoteObject) => {
                  return (
                    <Box
                      key={quoteObject.timestamp}
                      sx={{
                        width: "100%",
                        mb: 2,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      <Quote
                        quoteObject={quoteObject}
                        quotes={insights.quotes}
                        refetch={refetch}
                        category={Category.QUOTES}
                      />
                    </Box>
                  );
                })}
            </TabPanel>
            <TabPanel
              value={selectedTabIndex}
              index={1}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 1
              }
            >
              {insights &&
                insights.titles.length > 0 &&
                insights.titles.map((title) => {
                  return (
                    <Title
                      key={title}
                      category={Category.TITLES}
                      title={title}
                      titles={insights.titles}
                      refetch={refetch}
                    />
                  );
                })}
            </TabPanel>
            <TabPanel
              value={selectedTabIndex}
              index={2}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 2
              }
            >
              {insights &&
                insights.tweets.length > 0 &&
                insights.tweets.map((tweet) => {
                  return (
                    <Title
                      key={tweet}
                      category={Category.TWEETS}
                      title={tweet}
                      titles={insights.tweets}
                    />
                  );
                })}
            </TabPanel>
            <TabPanel
              value={selectedTabIndex}
              index={3}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 3
              }
            >
              {insights &&
                insights.chapters.length > 0 &&
                insights.chapters.map((chapter) => {
                  return (
                    <ChapterView
                      key={chapter.title}
                      chapters={insights.chapters}
                      chapterObject={chapter}
                      category={Category.CHAPTERS}
                      refetch={handleReloadData}
                    />
                  );
                })}
            </TabPanel>
            <TabPanel
              value={selectedTabIndex}
              index={4}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 4
              }
            >
              {insights &&
                insights.keywords.length > 0 &&
                insights.keywords.map((keyword) => {
                  return (
                    <Title
                      key={keyword}
                      category={Category.KEYWORDS}
                      title={`#${keyword}`}
                      titles={insights.keywords}
                    />
                  );
                })}
            </TabPanel>
            <TabPanel
              value={selectedTabIndex}
              index={5}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 5
              }
            >
              {insights && insights.blog_post && (
                // eslint-disable-next-line react/no-children-prop
                <ContentPost
                  title="Blog Post"
                  content={insights.blog_post}
                  refetch={refetch}
                  category={Category.BLOGPOST}
                />
              )}
            </TabPanel>
            <TabPanel
              value={selectedTabIndex}
              index={6}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 6
              }
            >
              {insights && insights.newsletter && (
                // eslint-disable-next-line react/no-children-prop, react/jsx-no-undef
                <NewsletterPost
                  title="Newsletter"
                  content={insights.newsletter}
                  refetch={refetch}
                  category={Category.NEWSLETTER}
                />
              )}
            </TabPanel>
            <TabPanel
              value={selectedTabIndex}
              index={7}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 7
              }
            >
              {insights &&
                insights.linkedin_posts.length > 0 &&
                insights.linkedin_posts.map((linkedinPost) => {
                  return (
                    <Title
                      key={linkedinPost}
                      category={Category.LINKEDINPOSTS}
                      title={linkedinPost}
                      titles={insights.linkedin_posts}
                    />
                  );
                })}
            </TabPanel>
            <TabPanel
              value={selectedTabIndex}
              index={8}
              isRegeneratingInsights={
                isPending && regeneratingInsightsForIndex === 8
              }
            >
              {insights &&
                insights.descriptions.length > 0 &&
                insights.descriptions.map((description) => {
                  return (
                    <Title
                      category={Category.DESCRIPTIONS}
                      key={description}
                      title={description}
                      titles={insights.descriptions}
                    />
                  );
                })}
            </TabPanel>
          </Box>
        </Box>
        {selectedTab && (
          <RegenerateInsightsDialog
            customPrompt={customPrompt}
            setCustomPrompt={setCustomPrompt}
            selectedTab={selectedTab}
            open={isRegenerateInsightsModalOpen}
            onClose={() => setIsRegenerateInsightsModalOpen(false)}
            onRegenerate={handleRegenerateInsights}
            hasCredits={hasCredits}
            loading={isPending}
          />
        )}
      </Box>
    </>
  );
}
